import React, { useState, useEffect } from 'react'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useLazyQuery } from '@apollo/client'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { EyeIcon, ArrowLeftIcon, PencilIcon, PhotoIcon } from '@heroicons/react/20/solid'
import { GET_APP_BANNERS } from 'graphql/common/queries'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { BoatIcon } from 'components/icons'
import { formatDateOnly } from 'utils/common'
import { getPageRange } from 'utils/paginationHelper'

export default function AppBanners() {
    const navigate = useNavigate()
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const [bannerList, setBannerList] = useState(null)
    const [bannerCount, setBannerCount] = useState(0)

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(bannerCount / rowsPerPage)

    const [getBannerList, { loading: bannerListLoading, error: bannerListError }] = useLazyQuery(GET_APP_BANNERS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getAppBanners.success && data.getAppBanners.code === '200') {
                setBannerList(data.getAppBanners.app_banners)
                setBannerCount(data.getAppBanners.total_count)
            }
        },
        onError: error => {
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    // Search boats Boat List
    const searchBanner = () => {
        getBannerList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                bannerFilter: {
                    id: null,
                    valid_from: null,
                    valid_to: null,
                    is_active: null,
                },
            },
        })
    }

    useEffect(() => {
        searchBanner()
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
        setShowErrDialog(false)
        setErrorMessage('')
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage App Banner Images')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Manage and customize app banners for an engaging user experience')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <button
                                    onClick={() => navigate('/app-banners/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Banner')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {bannerListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {bannerListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch banner!')}</div>
                </div>
            )}
            {!bannerListLoading && !bannerListError && bannerList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!bannerListLoading && !bannerListError && bannerList?.length > 0 && (
                <>
                    <Divider className="mt-5" />
                    <div className="table-custom flex justify-center items-center bg-white p-0">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Sno')}</TableHeader>
                                    <TableHeader>{t('Image')}</TableHeader>
                                    <TableHeader>{t('Validity')}</TableHeader>
                                    <TableHeader>{t('Active')}</TableHeader>
                                    <TableHeader className="flex justify-end">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bannerList.map((banner, index) => (
                                    <TableRow
                                        key={banner.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        // href={`/app-banners/add/${banner.id}`}
                                        onClick={() => navigate(`/app-banners/add/${banner.id}`)}
                                        title={`Boat #${banner.id}`}
                                    >
                                        <TableCell>
                                            <span className="text-sm font-semibold text-gray-900">{index + 1}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                <ImageDisplay
                                                    s3Key={banner.image?.mobile.s3_key}
                                                    file=""
                                                    classStyle="w-[180px] h-[50px] object-cover rounded-md border-[1px] border-primary/[.3]"
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {formatDateOnly(banner.valid_from)} -{' '}
                                                    {formatDateOnly(banner.valid_to)}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={banner.is_active ? 'emerald' : 'rose'}>
                                                {banner.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    className="cursor-pointer"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    {/* <DropdownItem href={`/app-banners/details/${banner.id}`}>
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider /> */}
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/app-banners/add/${banner.id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}
        </>
    )
}
